import { onError } from "@apollo/client/link/error";
import * as Sentry from "@sentry/react";
import { isAbortedNetworkError } from "shared/src/utils/apolloErrorUtils";

export const apolloErrorLink = onError(
  ({ graphQLErrors, networkError, operation }) => {
    if (graphQLErrors) {
      graphQLErrors.forEach((error) => {
        const errorInfo = {
          type: "GraphQL Error",
          message: error.message,
          path: error.path,
          extensions: error.extensions,
          operation: operation.operationName,
          variables: operation.variables,
        };

        const sentryError = new Error(
          `GraphQL Error in ${operation.operationName || "Unknown Operation"}: ${error.message}`,
        );
        sentryError.name = "GraphQLError";

        Sentry.captureException(sentryError, {
          tags: {
            errorType: "graphql",
            operation: operation.operationName || "unknown",
            source: "apollo-error-link",
          },
          extra: errorInfo,
          fingerprint: [
            "graphql-error",
            operation.operationName || "unknown",
            error.message,
          ],
        });
      });
    }

    if (networkError) {
      const errorType =
        "statusCode" in networkError
          ? `Network Error (${networkError.statusCode})`
          : "Network Error";

      const errorInfo = {
        type: errorType,
        message: networkError.message,
        operation: operation.operationName,
        variables: operation.variables,
      };

      if (isAbortedNetworkError(networkError)) {
        // oxlint-disable-next-line no-console
        console.warn(
          `[Skipped Sentry Report]: ${operation.operationName || "Unknown Operation"} - ${networkError.message}`,
        );
        return;
      }

      const sentryError = new Error(
        `${errorType} in ${operation.operationName || "Unknown Operation"}: ${networkError.message}`,
      );
      sentryError.name = errorType.replace(/[()]/g, "").replace(/\s+/g, "");

      Sentry.captureException(sentryError, {
        tags: {
          errorType: "network",
          operation: operation.operationName || "unknown",
          source: "apollo-error-link",
          ...("statusCode" in networkError && {
            statusCode: String(networkError.statusCode),
          }),
        },
        extra: errorInfo,
        fingerprint: [
          "network-error",
          operation.operationName || "unknown",
          errorType,
        ],
      });
    }
  },
);

export function createEnhancedApolloErrorLink() {
  return apolloErrorLink;
}
